import { apiClient } from '@/infrastructure/api'
import {
  ProcessesApiFindExportLineItemsRequest,
  ProcessesApiFindLineItemsRequest
} from '@itsrever/dashboard-api'
import {
  ProcessesApi,
  ProcessesApiGetLogisticsSummaryRequest,
  PbFindProcessesRequest,
  ProcessesApiGetAuthorizationStatusRequest,
  ProcessesApiGetWfpClaimByReturnProcessIDRequest,
  ProcessesApiFindProcessCountriesRequest,
  ProcessesApiFindProcessCarriersRequest,
  ProcessesApiFindLineItemReturnReasonsRequest,
  ProcessesApiFindProcessWarehousesRequest,
  ProcessesApiFindProcessesRequest as ProcessesApiFindProcessesV2Request,
  PbWithdrawalRequest
} from '@itsrever/rever-api'
import { ProcessesApi as DashboardApiProcessesApi } from '@itsrever/dashboard-api'

export const processesApi = new ProcessesApi(undefined, undefined, apiClient)
const dashboardApiProcessesApi = new DashboardApiProcessesApi(
  undefined,
  undefined,
  apiClient
)

export async function getClaimStatus(
  req: ProcessesApiGetWfpClaimByReturnProcessIDRequest
) {
  const response = await processesApi.getWfpClaimByReturnProcessID(req)
  return response.data
}

export async function getProcessCountries(
  args: ProcessesApiFindProcessCountriesRequest,
  signal?: AbortSignal
) {
  const response = await processesApi.findProcessCountries(args, { signal })
  return response.data
}

export async function getProcessCarriers(
  args: ProcessesApiFindProcessCarriersRequest,
  signal?: AbortSignal
) {
  const response = await processesApi.findProcessCarriers(args, { signal })
  return response.data
}

export async function getLineItemReturnReasons(
  args: ProcessesApiFindLineItemReturnReasonsRequest,
  signal?: AbortSignal
) {
  const response = await processesApi.findLineItemReturnReasons(args, {
    signal
  })
  return response.data
}

export async function getProcessWarehouses(
  args: ProcessesApiFindProcessWarehousesRequest,
  signal?: AbortSignal
) {
  const response = await processesApi.findProcessWarehouses(args, { signal })
  return response.data
}

export async function findProcesses(
  args: ProcessesApiFindProcessesV2Request,
  signal?: AbortSignal
) {
  const response = await processesApi.findProcesses(args, { signal })
  return response.data
}

export async function getExportProcessesCsv(filters: PbFindProcessesRequest) {
  const response = await processesApi.exportReturnProcessesCsv({
    pbExportReturnProcessesCsvRequest: {
      filters
    }
  })
  return response.data
}

export async function requestUploadReturnDocuments(
  processId: string,
  message?: string
) {
  const response = await processesApi.requestCustomerUploadPhotos({
    processId,
    pbRequestCustomerUploadPhotosRequest: {
      process_id: processId,
      ecommerce_message: message ?? ''
    }
  })
  return response.data
}

export async function getProcessLogistics(
  args: ProcessesApiGetLogisticsSummaryRequest,
  signal?: AbortSignal
) {
  const getProcessLogisticsResponse = await processesApi.getLogisticsSummary(
    args,
    { signal }
  )
  return getProcessLogisticsResponse.data
}

// Dashboard Api Calls for Processes
export async function getExportLineItems(
  args?: ProcessesApiFindExportLineItemsRequest
) {
  const getLineItemsResponse =
    await dashboardApiProcessesApi.findExportLineItems(args)
  return getLineItemsResponse.data
}

export async function getLineItems(args: ProcessesApiFindLineItemsRequest) {
  const getLineItemsResponse =
    await dashboardApiProcessesApi.findLineItems(args)
  return getLineItemsResponse.data
}

export async function getAuthorizationStatus(processId: string) {
  const args = {
    processId
  } as ProcessesApiGetAuthorizationStatusRequest
  const getAuthorizationStatusResponse =
    await processesApi.getAuthorizationStatus(args)
  return getAuthorizationStatusResponse.data
}

const WITHDRAWALS_MAX_PAGE = 100

export async function listWithdrawalRequests(
  ecommerceId: string,
  offset: number,
  limit: number
) {
  const { data } = await processesApi.listWithdrawalRequests({
    ecommerceId,
    offset,
    limit
  })
  return data
}

export async function setWithdrawalRequestClosed(
  ecommerceId: string,
  id: string,
  closed: boolean
) {
  const { data } = await processesApi.setWithdrawalRequestClosed({
    id,
    pbSetWithdrawalRequestClosedRequest: { ecommerce_id: ecommerceId, closed }
  })
  return data
}

// Every request of the store, a page at a time: the API returns 100 at most.
export async function listAllWithdrawalRequests(ecommerceId: string) {
  const all: PbWithdrawalRequest[] = []
  for (;;) {
    const page = await listWithdrawalRequests(
      ecommerceId,
      all.length,
      WITHDRAWALS_MAX_PAGE
    )
    all.push(...(page.withdrawal_requests ?? []))
    if (
      !page.withdrawal_requests?.length ||
      all.length >= Number(page.rowcount ?? 0)
    )
      return all
  }
}
